import * as React from 'react';
import {Route, Switch, Redirect} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {User} from 'nutshell-core/types';

import {resyncSidebarCollapseClasses} from '../resync-sidebar-collapse-classes';
import {isPathAllowedWhenExpired} from '../routes/shared/expired-billing-redirect';
import {RefreshingRoute} from '../routes/shared/refreshing-route';
import {NavigationListener} from '../routes/utils';
import {DemioPage} from '../ui/demio/demio-page';
import CustomDragLayer from '../ui/dnd/custom-drag-layer';
import {EmailRoot} from '../ui/email/email-root';
import {EmailMarketingRoot} from '../ui/email-marketing/email-marketing-root';
import {NoAccessPage} from '../ui/email-marketing/no-access/no-access-page';
import {EntityListWrapper} from '../ui/entity-list/entity-list-wrapper';
import {InboxNoAccessPage} from '../ui/inbox/no-access/inbox-no-access-page';
import {NavbarPortalled} from '../ui/navigation/navbar/navbar-portalled';
import {SalesDemoPage} from '../ui/navigation/trial-banner/sales-demo-page';
import {TrialBannerPortalled} from '../ui/navigation/trial-banner/trial-banner-portalled';
import {NoAccessRoot} from '../ui/no-access/no-access-root';
import {AccountLayoutCustomizer} from '../ui/peep/account/account-layout-customizer/account-layout-customizer';
import {ContactLayoutCustomizer} from '../ui/peep/contact/contact-layout-customizer/contact-layout-customizer';
import {ReportsRoot} from '../ui/reports/reports-root';
import {RouteContext} from '../ui/route-context';
import {MyAccountRoot} from '../ui/setup/my-account-root';
import {SetupRoot} from '../ui/setup/setup-root';
import {SidebarPortalled} from '../ui/sidebar/sidebar-portalled';
import {SupportDashboardPage} from '../ui/support-dashboard/support-dashboard-page';
import {TaskView} from '../ui/tasks/task-view';
import {TeamAiPortalled} from '../ui/team-ai';

import {AiHomeRoot} from './ai-home/ai-home-root';
import {AppRoutesModals} from './app-routes-modals';
import {DebugRoot} from './debug/debug-root';
import {EmailDigestRoot} from './email-digest/email-digest-root';
import {EngagementRoot} from './engagement/engagement-root';
import {ExploreRoot} from './explore/explore-root';
import {useGetPersistedQueryParams} from './graphql/use-get-persisted-query-params';
import {LeadPageRoot} from './leads/lead-page-root';
import {MarketingRoot} from './marketing/marketing-root';
import {NotePageRoot} from './notes/note-page-root';
import {PeepPageRoot} from './peep/peep-page-root';
import {SalesRoot} from './sales/sales-root';
import {RestrictedRoute} from './shared/restricted-route';
import {TicketMessageRoot} from './ticket-message/ticket-message-root';
import {TimelineRoot} from './timeline/timeline-root';

type Props = ContextRouter & {
    isInitialized: boolean;
    user: User | null | undefined;
    contacts: any;
    leads: any;
    accounts: any;
    notifications: any[];
};

export function AppRoutes(props: Props) {
    const {isInitialized, user, history, location} = props;

    const previousLocation = React.useRef(location);

    // We need this! Supports our 'remember last view'
    // feature for list views
    useGetPersistedQueryParams();

    // set previousLocation if props.location is not modal
    if (!location.state || !location.state.modal) {
        previousLocation.current = location;
    }

    const isModal = Boolean(location.state && location.state.modal);
    const isRecentlyAdded = Boolean(user && user.isRecentlyAdded);

    React.useEffect(() => {
        if (user) {
            resyncSidebarCollapseClasses(user.isMainSidebarCollapsed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialized]);

    return (
        <RouteContext.Provider value={{isEmbedded: false}}>
            <Switch
                location={
                    // This keeps the page underneath the modal unchanged
                    isModal ? previousLocation.current : props.location
                }
            >
                {/* Redirect to billing if account is expired and on a non-allowed path */}
                {NutClientConfig.isAccountExpired &&
                    !isPathAllowedWhenExpired(location.pathname) && (
                        <Redirect to='/setup/billing/' />
                    )}
                {/* Sidebar navigation */}
                <RefreshingRoute
                    path='/sales'
                    render={() => {
                        return (
                            <SalesRoot
                                prevLocation={isModal ? previousLocation.current : undefined}
                                user={user}
                            />
                        );
                    }}
                />
                {user && user.permissions.canAccessMarketing && (
                    <Redirect exact={true} from='/marketing/no-access' to='/marketing/dashboard' />
                )}
                {user && !user.permissions.canAccessMarketing && (
                    <Redirect exact={true} from='/marketing/dashboard' to='/marketing/no-access' />
                )}
                <RefreshingRoute path='/marketing/no-access' render={() => <NoAccessPage />} />
                <RefreshingRoute path='/marketing' render={() => <MarketingRoot user={user} />} />
                {/* Even though we don't use this root name in the new world, we need
                to keep the root available so we can redirect any links from
                /email-marketing to /marketing */}
                <RefreshingRoute
                    path='/email-marketing/no-access'
                    render={() => <NoAccessPage />}
                />
                <RefreshingRoute
                    path='/email-marketing'
                    render={() => <EmailMarketingRoot {...props} />}
                />
                {/* TODO: update to /engagement */}
                {user && user.permissions.canUseOrTryInbox && (
                    <Redirect exact={true} from='/inbox/no-access' to='/inbox/dashboard' />
                )}
                {user && !user.permissions.canUseOrTryInbox && (
                    <Redirect exact={true} from='/inbox' to='/inbox/no-access' />
                )}
                <RefreshingRoute path='/inbox/no-access' render={() => <InboxNoAccessPage />} />
                <RefreshingRoute
                    path='/inbox'
                    render={(routeProps) => <EngagementRoot {...routeProps} user={user} />}
                />
                <RefreshingRoute path='/email-digest' render={() => <EmailDigestRoot />} />
                <RefreshingRoute
                    path='/companies'
                    render={(routeProps) => (
                        <EntityListWrapper
                            {...routeProps}
                            key='accounts'
                            entity={props.accounts}
                            notifications={props.notifications}
                            entityType='accounts'
                            isInitialized={isInitialized}
                        />
                    )}
                />
                <RefreshingRoute
                    path='/people'
                    render={(routeProps) => (
                        <EntityListWrapper
                            {...routeProps}
                            key='contacts'
                            entity={props.contacts}
                            notifications={props.notifications}
                            entityType='contacts'
                            isInitialized={isInitialized}
                        />
                    )}
                />
                <RefreshingRoute
                    path='/leads'
                    render={(routeProps) => (
                        <EntityListWrapper
                            {...routeProps}
                            {...props}
                            key='leads'
                            entity={props.leads}
                            notifications={props.notifications}
                            entityType='leads'
                            isInitialized={isInitialized}
                            // @ts-expect-error
                            tour={routeProps.location.state && routeProps.location.state.tour}
                        />
                    )}
                />
                <RefreshingRoute
                    path='/reports'
                    render={(routeProps) => (
                        <ReportsRoot
                            user={user}
                            // @ts-expect-error
                            tour={routeProps.location.state && routeProps.location.state.tour}
                        />
                    )}
                />
                <RefreshingRoute path='/ai/home' render={() => <AiHomeRoot />} />
                <RefreshingRoute path='/explore' render={() => <ExploreRoot />} />
                <RefreshingRoute path='/setup' render={() => <SetupRoot />} />
                <RefreshingRoute path='/my-account' render={() => <MyAccountRoot />} />
                {/* Top navigation */}
                <RefreshingRoute
                    path='/email'
                    render={(routeProps) => <EmailRoot {...routeProps} />}
                />

                {/* Lead/company/person pages */}
                <RefreshingRoute
                    path='/lead/id/:leadId'
                    render={(routeProps) => <LeadPageRoot {...routeProps} user={user} />}
                />
                <RefreshingRoute
                    path='/lead/:leadNum'
                    exact={true}
                    render={(routeProps) => <LeadPageRoot {...routeProps} user={user} />}
                />
                <RestrictedRoute
                    path={'/person/customize-layout'}
                    render={() => <ContactLayoutCustomizer />}
                    hasAccess={NutClientConfig.hasPeepPagesRedesignFF}
                    redirectPath='/people'
                />
                <RefreshingRoute
                    path='/person/:peepSlug'
                    exact={true}
                    render={(routeProps) => (
                        <PeepPageRoot {...routeProps} user={user} entityType='person' />
                    )}
                />
                <Redirect exact={true} from='/contact/:peepSlug' to='/person/:peepSlug' />
                <RestrictedRoute
                    path={'/company/customize-layout'}
                    render={() => <AccountLayoutCustomizer />}
                    hasAccess={NutClientConfig.hasPeepPagesRedesignFF}
                    redirectPath='/companies'
                />
                <RefreshingRoute
                    path='/company/:peepSlug'
                    exact={true}
                    render={(routeProps) => (
                        <PeepPageRoot {...routeProps} user={user} entityType='company' />
                    )}
                />
                <Redirect exact={true} from='/account/:peepSlug' to='/company/:peepSlug' />
                <RefreshingRoute
                    path='/timeline'
                    // @ts-expect-error
                    render={(routeProps) => <TimelineRoot {...routeProps} />}
                />
                <RefreshingRoute
                    path='/ticketmessage/:ticketMessageId'
                    render={(routeProps) => (
                        <TicketMessageRoot
                            ticketMessageId={routeProps.match.params.ticketMessageId}
                        />
                    )}
                />
                <RefreshingRoute
                    path='/note/:noteId'
                    render={(routeProps) => (
                        <NotePageRoot noteId={routeProps.match.params.noteId} />
                    )}
                />
                <RefreshingRoute path='/tasks/:id/:action?' render={() => <TaskView />} />
                {NutClientConfig.isInternalLogin && (
                    <RefreshingRoute path='/debug' render={() => <DebugRoot />} />
                )}
                {/* Less traveled pages */}
                <RefreshingRoute
                    path='/support'
                    render={(routeProps) => (
                        <SupportDashboardPage
                            {...routeProps}
                            viewUrl={routeProps.location.pathname}
                            viewParams={routeProps.location.search}
                        />
                    )}
                />
                <RefreshingRoute
                    path='/demio'
                    render={(routeProps) => (
                        <DemioPage
                            {...routeProps}
                            viewUrl={routeProps.location.pathname}
                            viewParams={routeProps.location.search}
                        />
                    )}
                />
                <RefreshingRoute path='/trash' render={() => <SetupRoot />} />
                <RefreshingRoute
                    path='/no-access'
                    render={() => <NoAccessRoot shouldWelcome={isRecentlyAdded} />}
                />
                <Route path='/sales-demo' component={SalesDemoPage} />
                {/* This is a catch-all route that will redirect to the error controller if no route is found */}
                <Route
                    render={() => {
                        window.location.href = `/error?client-route=${window.location.href}`;

                        return null;
                    }}
                />
            </Switch>
            <Switch>
                <AppRoutesModals history={history} location={location} user={user} />
            </Switch>
            <Route path='/' component={NavigationListener} />
            <CustomDragLayer />
            {window.location.pathname === '/timeline' ? undefined : (
                <>
                    <SidebarPortalled sidebarMountId='sidebar' />
                    <TeamAiPortalled teamAiMountId='team-ai-sidebar' />
                    <NavbarPortalled navbarMountId='navbar' user={user} />
                    <TrialBannerPortalled trialBannerMountId='trial-banner' />
                </>
            )}
        </RouteContext.Provider>
    );
}
